import { createContext } from 'react';
import type { ImportCandidate, ItemImport, ItemImportProgress } from 'src/queries/ItemImport';
import type { ImportPhase } from '../../../_routes/Job/JobReview/ItemImport/useItemImport';

/**
 * A source photo/spreadsheet import, tracked globally rather than inside the
 * job it was started from.
 *
 * The whole point of this context is that an import outlives the component
 * tree that started it: once extraction begins, the reviewer can navigate
 * anywhere in the app (or close the tab's route entirely) and the import
 * keeps running, tracked here instead of in local state that would unmount
 * with the page. `jobKey` is what lets a global progress toast say which
 * job to return to, and lets each job's own Inventory screen tell
 * whether an in-flight import belongs to it.
 *
 * A spreadsheet goes one step further: past its upload it is owned by the
 * server, and this context only *watches* it while the modal is open. The
 * job's `ImportProgressBanner` is what shows it the rest of the time, and
 * `resume` is how the banner hands one back to the modal for review.
 */
export type PendingImport = {
  jobKey: string;
  source: 'belongings' | 'lists' | 'spreadsheet';
  files: File[];
};

export type ItemImportContextType = {
  /** Set as soon as files are picked, cleared once `start` hands off to the upload. */
  pendingImport: PendingImport | null;
  beginPendingImport: (jobKey: string, source: PendingImport['source'], files: File[]) => void;
  /** Clears a pending import before it ever uploads (the setup step's own Cancel). */
  cancelPendingImport: () => void;

  /** The job the currently-running (started) import belongs to, if any. */
  jobKey: string | null;
  /** The import being watched, once its upload has returned (or since `resume`). */
  importKey: string | null;
  /** Set alongside jobKey by `start`, kept for the run's whole lifetime. */
  source: PendingImport['source'] | null;
  phase: ImportPhase;
  processed: number;
  total: number;
  found: number;
  /** The API's own progress (rows read, ETA) for the import being watched. */
  progress: ItemImportProgress | null;
  candidates: ImportCandidate[];
  setCandidates: (candidates: ImportCandidate[]) => void;
  error: string | null;
  autoShop: boolean;
  setAutoShop: (autoShop: boolean) => void;
  start: (jobKey: string, source: PendingImport['source'], upload: () => Promise<ItemImport>) => Promise<void>;
  /** Reopens an existing spreadsheet import (the banner's "Review items") in the modal. */
  resume: (jobKey: string, importKey: string) => void;
  commit: () => Promise<void>;
  /**
   * Closes the modal. A spreadsheet that has reached the server is only
   * stopped being *watched* — it keeps going, on the banner. Anything else
   * (photos, lists, or a spreadsheet that never finished uploading) is
   * abandoned, as `cancel`.
   */
  close: () => void;
  /** Abandons the run entirely — deletes an uncommitted import. */
  cancel: () => void;

  /** True while the modal is tucked into the global progress toast instead of shown. */
  isMinimized: boolean;
  minimize: () => void;
  restore: () => void;
};

const noop = () => undefined;

export const ItemImportContext = createContext<ItemImportContextType>({
  pendingImport: null,
  beginPendingImport: noop,
  cancelPendingImport: noop,
  jobKey: null,
  importKey: null,
  source: null,
  phase: 'idle',
  processed: 0,
  total: 0,
  found: 0,
  progress: null,
  candidates: [],
  setCandidates: noop,
  error: null,
  autoShop: true,
  setAutoShop: noop,
  start: async () => undefined,
  resume: noop,
  commit: async () => undefined,
  close: noop,
  cancel: noop,
  isMinimized: false,
  minimize: noop,
  restore: noop,
});
